import type { ButtonHTMLAttributes } from "react";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "success";
export type ButtonSize = "sm" | "md";

const base =
  "inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed";

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-primary text-white hover:bg-primary-hover hover:text-white",
  secondary:
    "bg-surface text-text border border-border hover:border-border-strong hover:bg-surface-alt hover:text-text",
  ghost: "bg-transparent text-text-muted hover:bg-surface-alt hover:text-text",
  danger:
    "bg-danger-soft text-[#b91c1c] border border-[#fecaca] hover:bg-[#fbdada] hover:text-[#b91c1c]",
  success:
    "bg-success-soft text-[#166534] border border-[#bbf7d0] hover:bg-[#d5f5df] hover:text-[#166534]",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-[13px]",
  md: "px-4 py-2.5 text-sm",
};

export function buttonClass(opts: { variant?: ButtonVariant; size?: ButtonSize } = {}): string {
  return `${base} ${VARIANTS[opts.variant ?? "primary"]} ${SIZES[opts.size ?? "md"]}`;
}

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  loadingLabel?: string;
};

export function Button({
  variant,
  size,
  loading,
  loadingLabel = "Submitting…",
  disabled,
  children,
  className = "",
  ...rest
}: Props) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={`${buttonClass({ variant, size })} ${className}`}
    >
      {loading ? loadingLabel : children}
    </button>
  );
}
